import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useFetcher } from "@remix-run/react";

type TickerSearchModalProps = {
    showSearchModal: boolean;
    setShowSearchModal: (showSearchModal: boolean) => void;
    title?: string;
    mode: "financials" | "snapshots" | "insider-activity" | "news" | null | undefined;
};

const modeConfig: Record<string, { mode: "financials" | "snapshots" | "insider-activity" | "news" | null, title: string; placeholder: string }> = {
    financials: {
        mode: "financials",
        title: "Jump to Financials",
        placeholder: "Select Ticker"
    },
    snapshots: {
        mode: "snapshots",
        title: "Jump to Snapshots",
        placeholder: "Select Ticker"
    },
    "insider-activity": {
        mode: "insider-activity",
        title: "Jump to Insider Activity",
        placeholder: "Select Ticker"
    },
    news: {
        mode: "news",
        title: "Jump to News & Sentiment",
        placeholder: "Select Ticker"
    }
    // Add more modes here as needed
};

const TickerSearchModal: React.FC<TickerSearchModalProps> = ({
    showSearchModal,
    setShowSearchModal,
    mode
}) => {

    let title = "Search Your Ticker";
    const fetcher = useFetcher();
    const [suggestions, setSuggestions] = useState<Array<{
        ticker_symbol: string;
        name: string;
        market_cap: number;
        logo_url: string;
        ticker_id: string;
        exchange: string;
        company_id: string;
    }>>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const navigate = useNavigate();
    const [isFocused, setIsFocused] = useState(false);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Determine title and placeholder based on mode
    const dynamicTitle = mode && modeConfig[mode]?.title ? modeConfig[mode].title : (title || "Search");
    const dynamicPlaceholder = mode && modeConfig[mode]?.placeholder ? modeConfig[mode].placeholder : "Search for a stock...";

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev > 0 ? prev - 1 : -1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0) {
                    handleSelectStock(suggestions[activeIndex]);
                }
                break;
            case 'Escape':
                setSuggestions([]);
                setActiveIndex(-1);
                break;
        }
    };

    const handleSelectStock = (stock: { ticker_id: string; exchange: string; ticker_symbol: string; company_id: string; }) => {
        console.log(stock);
        setSuggestions([]);
        setActiveIndex(-1);
        setShowSearchModal(false);

        const params = new URLSearchParams({
            type: "company",
            cid: stock.company_id,
        });
        if (mode) {
            params.append("active-panel", mode);
        }
        navigate(
            `/ticker/${stock.ticker_id}/${stock.exchange}/${stock.ticker_symbol}?${params.toString()}`,
            { replace: false, state: { needRefresh: true } }
        );
    };

    const handleSearch = debounce(async (value: string) => {
        if (!value) {
            setSuggestions([]);
            return;
        }

        fetcher.load(`/api/tickers?search_term=${encodeURIComponent(value)}`);
    }, 300);

    useEffect(() => {
        if (fetcher.data && Array.isArray(fetcher.data)) {
            const newSuggestions = fetcher.data.slice(0, 5);
            setSuggestions(newSuggestions);
            // Ensure dropdown is visible when suggestions arrive if modal is open and input is focused
            if (newSuggestions.length > 0 && showSearchModal) {
                // Check if input is actually focused
                if (inputRef.current === document.activeElement) {
                    setIsFocused(true);
                } else {
                    // If input isn't focused but modal is open, focus it
                    inputRef.current?.focus();
                    setIsFocused(true);
                }
            }
        }
    }, [fetcher.data, showSearchModal]);

    // useEffect(() => {
    //     if (showSearchModal && inputRef.current) {
    //         // Use requestAnimationFrame to preserve user interaction context
    //         // Single requestAnimationFrame should be sufficient with setTimeout fallback
    //         requestAnimationFrame(() => {
    //             if (inputRef.current) {
    //                 // On mobile, click() often works better than focus() for opening keyboard
    //                 // Try click first, then focus
    //                 inputRef.current.click();
    //                 inputRef.current.focus();
    //                 setIsFocused(true);

    //                 // Additional attempt after a brief delay (helps with slower devices/Dialog animations)
    //                 setTimeout(() => {
    //                     if (inputRef.current && document.activeElement !== inputRef.current) {
    //                         inputRef.current.click();
    //                         inputRef.current.focus();
    //                     }
    //                 }, 50);
    //             }
    //         });
    //     } else {
    //         setIsFocused(false);
    //         setSuggestions([]);
    //         setActiveIndex(-1);
    //     }
        
    //     // Cleanup timeout on unmount or modal close
    //     return () => {
    //         if (blurTimeoutRef.current) {
    //             clearTimeout(blurTimeoutRef.current);
    //             blurTimeoutRef.current = null;
    //         }
    //     };
    // }, [showSearchModal]);
// Try to keep logic minimal for iOS: one delayed .focus() with setSelectionRange
useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (showSearchModal && inputRef.current) {
        timeout = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus({ preventScroll: true });
                // Setting selection range helps open the keyboard on iOS
                const len = inputRef.current.value?.length ?? 0;
                inputRef.current.setSelectionRange(len, len);
                setIsFocused(true);
            }
        }, 60); // Delayed focus increases reliability on iOS
    } else {
        setIsFocused(false);
        setSuggestions([]);
        setActiveIndex(-1);
    }

    return () => {
        if (timeout) clearTimeout(timeout);
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
    };
}, [showSearchModal]);

useEffect(() => {
    // Cleanup timeout on unmount or modal close
    return () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
    };
}, []);

    return (
        <div className="search-modal">
            <Dialog open={showSearchModal} onClose={() => { }} className="relative z-30">
                <DialogBackdrop
                    className="fixed inset-0 bg-gray-500/75"
                />

                <div className="fixed inset-0 w-screen overflow-y-auto pt-20">
                    <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-start sm:p-0">
                        <DialogPanel
                            className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 w-[95%] sm:w-full sm:max-w-lg sm:p-6"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold text-gray-900">{dynamicTitle}</h2>
                                <button
                                    type="button"
                                    onClick={() => setShowSearchModal(false)}
                                    className="rounded-md bg-white text-gray-900 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    <FontAwesomeIcon icon={faCircleXmark} className="size-6" />
                                </button>
                            </div>
                            <div className="mt-4 text-center sm:ml-4 sm:text-left">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-gray-900/20 rounded-full blur-md opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                                    <input
                                        ref={inputRef}
                                        autoFocus={true}
                                        tabIndex={0}
                                        type="text"
                                        id="search-bar-modal-text"
                                        className="relative w-full px-5 py-2 text-sm font-medium text-white 
                                                   bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900
                                                   border-2 border-gray-700 rounded-full 
                                                   shadow-2xl hover:shadow-xl hover:border-gray-600 hover:from-gray-700 hover:via-gray-800 hover:to-gray-900
                                                   focus:outline-none focus:ring-green-500/40 focus:shadow-2xl focus:shadow-green-500/20
                                                   focus:from-gray-700 focus:via-gray-800 focus:to-black
                                                   transition-all duration-300 pr-12 
                                                   placeholder:text-gray-400 placeholder:font-normal"
                                        placeholder={dynamicPlaceholder}
                                        onKeyDown={handleKeyDown}
                                        onFocus={() => {
                                            if (blurTimeoutRef.current) {
                                                clearTimeout(blurTimeoutRef.current);
                                                blurTimeoutRef.current = null;
                                            }
                                            setIsFocused(true);
                                        }}
                                        onBlur={(e) => {
                                            // Don't hide if clicking on suggestions dropdown
                                            const relatedTarget = e.relatedTarget as HTMLElement;
                                            if (suggestionsRef.current?.contains(relatedTarget)) {
                                                return;
                                            }
                                            blurTimeoutRef.current = setTimeout(() => {
                                                setIsFocused(false);
                                                setActiveIndex(-1);
                                            }, 200);
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value.toLowerCase();
                                            handleSearch(value);
                                            setActiveIndex(-1);
                                        }}
                                        autoComplete='off'
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <div className={`w-7 h-7 flex items-center justify-center rounded-full bg-gray-700 transition-all duration-300 ${isFocused ? 'bg-green-600/80' : ''}`}>
                                            <FontAwesomeIcon icon={faMagnifyingGlass} className={`text-gray-300 text-sm transition-all duration-300 ${isFocused ? 'text-white scale-110' : ''}`} />
                                        </div>
                                    </div>
                                    {suggestions.length > 0 && isFocused && (
                                        <div 
                                            ref={suggestionsRef}
                                            className="absolute w-full mt-1 bg-gray-900 border-2 border-gray-700 rounded-xl shadow-2xl overflow-y-auto z-50 max-h-60 backdrop-blur-md ring-2 ring-gray-700/50"
                                            onMouseDown={(e) => {
                                                // Prevent input blur when clicking on suggestions
                                                e.preventDefault();
                                            }}
                                        >
                                            {suggestions.map((stock: { ticker_symbol: string; name: string; market_cap: number; logo_url: string; ticker_id: string; exchange: string; company_id: string; }, index: number) => (
                                                <button
                                                    key={stock.ticker_symbol}
                                                    className={`w-full text-left px-4 py-2 hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-700 flex items-center transition-all duration-200 ${index === activeIndex ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-l-[3px] border-l-green-500 shadow-sm' : ''}`}
                                                    onMouseDown={(e) => {
                                                        // Prevent input blur
                                                        e.preventDefault();
                                                    }}
                                                    onClick={() => handleSelectStock(stock)}
                                                    role="option"
                                                    aria-selected={index === activeIndex}
                                                >
                                                    {stock.logo_url && (
                                                        <img
                                                            src={stock.logo_url}
                                                            alt={stock.name}
                                                            className="w-5 h-5 mr-2 flex-shrink-0"
                                                        />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`font-medium text-xs truncate ${index === activeIndex ? 'text-white' : 'text-gray-200'}`}>
                                                            {stock.ticker_symbol}
                                                        </div>
                                                        <div className={`text-xs truncate ${index === activeIndex ? 'text-gray-300' : 'text-gray-400'}`}>
                                                            {stock.name}
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}
export default TickerSearchModal;
